import { useEffect } from 'react'
import { BRANDING } from '@/config/branding'

/** Mix a hex colour toward white. ratio 0 = original, 1 = pure white. */
function tint(hex: string, ratio: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const m = (c: number) => Math.round(c + (255 - c) * ratio).toString(16).padStart(2, '0')
  return `#${m(r)}${m(g)}${m(b)}`
}

/** Mix a hex colour toward black. ratio 0 = original, 1 = pure black. */
function shade(hex: string, ratio: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const m = (c: number) => Math.round(c * (1 - ratio)).toString(16).padStart(2, '0')
  return `#${m(r)}${m(g)}${m(b)}`
}

/**
 * Generates a 10-stop Tailwind-style palette (50–900) from a single base hex.
 * The base colour is treated as the 500 level.
 * Works for any tenant — no hardcoded palettes needed.
 */
function generatePalette(base: string) {
  return {
    50:  tint(base, 0.92),
    100: tint(base, 0.84),
    200: tint(base, 0.68),
    300: tint(base, 0.50),
    400: tint(base, 0.28),
    500: base,
    600: shade(base, 0.18),
    700: shade(base, 0.35),
    800: shade(base, 0.52),
    900: shade(base, 0.68),
  }
}

interface Props { children: React.ReactNode }

/**
 * Applies tenant-specific CSS custom properties at mount from BRANDING config.
 * Full primary/secondary palettes (50–900) are generated at runtime from the
 * two base hex colours — enabling one build for any number of tenants with
 * zero hardcoded colour data.
 */
export function TenantThemeProvider({ children }: Props) {
  useEffect(() => {
    const root = document.documentElement
    const primary   = generatePalette(BRANDING.primaryColor)
    const secondary = generatePalette(BRANDING.secondaryColor)

    // Primary palette
    root.style.setProperty('--color-primary-50',  primary[50])
    root.style.setProperty('--color-primary-100', primary[100])
    root.style.setProperty('--color-primary-200', primary[200])
    root.style.setProperty('--color-primary-300', primary[300])
    root.style.setProperty('--color-primary-400', primary[400])
    root.style.setProperty('--color-primary',     primary[500])
    root.style.setProperty('--color-primary-600', primary[600])
    root.style.setProperty('--color-primary-700', primary[700])
    root.style.setProperty('--color-primary-800', primary[800])
    root.style.setProperty('--color-primary-900', primary[900])

    // Secondary palette
    root.style.setProperty('--color-secondary-50',  secondary[50])
    root.style.setProperty('--color-secondary-100', secondary[100])
    root.style.setProperty('--color-secondary-200', secondary[200])
    root.style.setProperty('--color-secondary-300', secondary[300])
    root.style.setProperty('--color-secondary-400', secondary[400])
    root.style.setProperty('--color-secondary',     secondary[500])
    root.style.setProperty('--color-secondary-600', secondary[600])
    root.style.setProperty('--color-secondary-700', secondary[700])
    root.style.setProperty('--color-secondary-800', secondary[800])
    root.style.setProperty('--color-secondary-900', secondary[900])

    // Derived dark colour (very deep primary) and header background
    root.style.setProperty('--color-dark', shade(BRANDING.primaryColor, 0.72))
    root.style.setProperty('--header-bg',  BRANDING.headerBackground)
    if (BRANDING.accentColor) {
      root.style.setProperty('--color-accent', BRANDING.accentColor)
    }
    if (BRANDING.footerAccentColor) {
      root.style.setProperty('--color-footer-accent', BRANDING.footerAccentColor)
    }

    // Legacy aliases kept for any inline styles that still reference them
    root.style.setProperty('--primary-blue',   secondary[500])
    root.style.setProperty('--secondary-teal', primary[500])

    document.title = `${BRANDING.appName} - Professional Learning Platform`
  }, [])

  return <>{children}</>
}
